import { type IntlShape } from "react-intl";
import { z } from "zod";
import { machineTypeSchema } from "@packsize/schemas/machines";

import { machineGroupStatusSchema } from "./machine-group-status";
import { productionStatusSchema } from "./production-status";

export const machineGroupSchema = z.object({
	id: z.string(),
	alias: z.string(),
	configuredMachinesWithMachineType: z.record(z.string(), machineTypeSchema).default({}),
	assignedOperator: z.string().nullish(),
	assignedOperatorId: z.string().nullish(),
	description: z.string().nullish(),
	productionStatus: productionStatusSchema,
	productionEnabled: z.boolean(),
	status: machineGroupStatusSchema,
	enableProductionAutomatically: z.boolean().nullish(),
	eventUtcTimeStamp: z.string().nullish(),
	productionDisabledByUser: z.boolean().nullish(),
	pullMode: z.boolean().nullish(),
	tenantId: z.string(),
});

export const deleteMachineGroupSchema = z.object({
	deleted: z.boolean(),
	failReason: z.string().nullish(),
});

export const machineGroupsResponseSchema = z.array(machineGroupSchema);

export const createMachineGroupSchema = machineGroupSchema.pick({
	alias: true,
	description: true,
	configuredMachinesWithMachineType: true,
	enableProductionAutomatically: true,
});

export type MachineGroup = z.infer<typeof machineGroupSchema>;
export type CreateMachineGroup = z.infer<typeof createMachineGroupSchema>;
export type DeleteMachineGroup = z.infer<typeof deleteMachineGroupSchema>;

export const getMachineGroupConfigurationSchema = (intl: IntlShape) =>
	z.object({
		alias: z
			.string(intl.formatMessage({ id: "Name needs to be a string", defaultMessage: "Name needs to be a string" }))
			.trim()
			.nonempty(intl.formatMessage({ id: "Name is required", defaultMessage: "Name is required" }))
			.max(
				100,
				intl.formatMessage({
					id: "Name must be less than 100 characters",
					defaultMessage: "Name must be less than 100 characters",
				}),
			),
		description: z
			.string(
				intl.formatMessage({
					id: "Description needs to be a string",
					defaultMessage: "Description needs to be a string",
				}),
			)
			.trim(),
		configuredMachinesWithMachineType: z.record(z.string(), machineTypeSchema),
		enableProductionAutomatically: z.boolean(),
	});

export type MachineGroupConfiguration = z.infer<ReturnType<typeof getMachineGroupConfigurationSchema>>;
